/*
  A/B swatch variant — color preview on collection cards.
  Loaded once by a-b-test-collection-product-grid.liquid.

  Desktop (hover-capable pointer):
    - hover / keyboard focus on a swatch previews that color's first image
    - leaving the swatch row restores the original image
    - clicking a swatch navigates to that color's PDP (normal link)

  Mobile / touch:
    - tapping a swatch selects that color: image swaps, active ring moves, and the
      card's link (image + title) now points to that color's PDP
    - tapping the image then opens the selected color's PDP
    - tapping the card's own color resets it

  Uses event delegation on document, so it keeps working for cards that
  facets.js re-renders (filter/sort) and that load-more.js appends — no re-init.
*/
(() => {
  const SWATCH = '.card-swatch';
  const ROW = '[data-card-swatches]';
  const CARD = '.grid__item';
  const CARD_LINK = 'a.product-card-wrapper';

  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  const carouselOf = (card) => card.querySelector('.card__media .flickity-enabled');

  // Image to swap: the slide Flickity is showing (slider mode), otherwise the
  // first visible slide (hover mode; skips slides the model/plain switcher hides).
  const targetImg = (card) => {
    const selected = card.querySelector('.card__media .flickity-enabled .is-selected img');
    if (selected) return selected;

    const medias = card.querySelectorAll('.card__media .media');
    for (const media of medias) {
      if (media.hidden || media.offsetParent === null) continue;
      const img = media.querySelector('img');
      if (img) return img;
    }
    return card.querySelector('.card__media img');
  };

  const restoreImg = (card) => {
    const img = card && card._swatchImg;
    if (!img || img.dataset.origSrc === undefined) return;
    if (img.dataset.origSrcset) img.setAttribute('srcset', img.dataset.origSrcset);
    else img.removeAttribute('srcset');
    img.setAttribute('src', img.dataset.origSrc);
    card.classList.remove('is-swatch-preview');
  };

  const showSwatchImg = (card, swatch) => {
    // The card's own color (or a swatch without an image) means "show the original".
    if (swatch.hasAttribute('data-swatch-current') || !swatch.dataset.swatchSrc) {
      restoreImg(card);
      return;
    }

    const img = targetImg(card);
    if (!img) return;

    // If a different image was swapped before (other slide), put that one back first.
    if (card._swatchImg && card._swatchImg !== img) restoreImg(card);

    if (img.dataset.origSrc === undefined) {
      img.dataset.origSrc = img.getAttribute('src') || '';
      img.dataset.origSrcset = img.getAttribute('srcset') || '';
    }
    img.setAttribute('srcset', swatch.dataset.swatchSrcset || '');
    img.setAttribute('src', swatch.dataset.swatchSrc);
    card.classList.add('is-swatch-preview');
    card._swatchImg = img;
  };

  // Warm the browser cache the first time a card is used, so the swap doesn't flicker.
  const preload = (card) => {
    if (!card || card.dataset.swatchesPreloaded) return;
    card.dataset.swatchesPreloaded = '1';
    card.querySelectorAll('[data-swatch-src]').forEach((s) => {
      const i = new Image();
      if (s.dataset.swatchSrcset) i.srcset = s.dataset.swatchSrcset;
      i.src = s.dataset.swatchSrc;
    });
  };

  // ---------- Desktop: hover preview ----------
  document.addEventListener('mouseover', (e) => {
    if (!canHover.matches) return;
    const card = e.target.closest(CARD);
    if (!card || !card.querySelector(ROW)) return;
    preload(card);

    const swatch = e.target.closest(SWATCH);
    if (swatch && swatch.closest(ROW)) showSwatchImg(card, swatch);
  });

  document.addEventListener('mouseout', (e) => {
    if (!canHover.matches) return;
    const row = e.target.closest(ROW);
    if (!row || row.contains(e.relatedTarget)) return;
    restoreImg(row.closest(CARD));
  });

  // Keyboard focus (desktop only — on touch, a tap also focuses the link and the
  // following focusout would undo the selection).
  document.addEventListener('focusin', (e) => {
    if (!canHover.matches) return;
    const swatch = e.target.closest(SWATCH);
    if (swatch && swatch.closest(ROW)) showSwatchImg(swatch.closest(CARD), swatch);
  });

  document.addEventListener('focusout', (e) => {
    if (!canHover.matches) return;
    const row = e.target.closest(ROW);
    if (!row || row.contains(e.relatedTarget)) return;
    restoreImg(row.closest(CARD));
  });

  // ---------- Mobile / touch: tap to select a color ----------
  document.addEventListener('pointerdown', (e) => {
    if (canHover.matches) return;
    const row = e.target.closest(ROW);
    if (row) preload(row.closest(CARD));
  });

  const selectColor = (card, swatch) => {
    // Show the first slide so the swapped image is the one in view.
    const carousel = carouselOf(card);
    const flkty = carousel && window.Flickity && window.Flickity.data(carousel);
    if (flkty && flkty.selectedIndex !== 0) flkty.select(0, false, true);

    showSwatchImg(card, swatch);

    // Point the card link (image + title) to the selected color's PDP.
    const link = card.querySelector(CARD_LINK);
    if (link) {
      if (link.dataset.origHref === undefined) link.dataset.origHref = link.getAttribute('href');
      link.setAttribute('href', swatch.getAttribute('href'));
    }

    // Move the active ring.
    card.querySelectorAll(`${ROW} ${SWATCH}`).forEach((s) => s.removeAttribute('aria-current'));
    swatch.setAttribute('aria-current', 'true');
  };

  // Capture phase: runs before the .grid__item click handler in the section, and
  // stopPropagation keeps a swatch tap from pushing a history entry each time.
  document.addEventListener(
    'click',
    (e) => {
      if (canHover.matches) return;
      const swatch = e.target.closest(SWATCH);
      if (!swatch || !swatch.closest(ROW)) return;
      e.preventDefault();
      e.stopPropagation();
      selectColor(swatch.closest(CARD), swatch);
    },
    true
  );
})();